import { lazy, Suspense, useMemo } from "react";
import { Loader2 } from "lucide-react";

// EChartsを動的インポート
const ReactECharts = lazy(() => import("echarts-for-react"));

export type ChartType = "line" | "bar" | "pie" | "roadmap" | "flowchart";

export interface ChartDataPoint {
  name: string;
  value: number;
  [key: string]: any;
}

export interface InteractiveChartProps {
  /** チャートタイプ */
  type: ChartType;
  /** データ */
  data?: ChartDataPoint[];
  /** タイトル */
  title?: string;
  /** 高さ */
  height?: string;
  /** カスタムオプション（EChartsオプションをそのまま渡す） */
  customOptions?: any;
}

// ベージュパレット（bg-second系）
const BEIGE = {
  50: "#f5f4ed",   // bg-second
  100: "#e6dbc9",
  200: "#cabca4",
  300: "#ada18c",
  400: "#938977",
  500: "#7b7263",
  600: "#6a6255",
  700: "#524c42",
  800: "#39352d",
};

// プログラミング言語選択チャート用のプリセット
function getLanguageSelectionFlowchart(): any {
  return {
    tooltip: { show: false },
    series: [
      {
        type: "tree",
        data: [
          {
            name: "何を作りたい？",
            itemStyle: { color: BEIGE[700] },
            children: [
              {
                name: "Webサイト・Webアプリ",
                itemStyle: { color: BEIGE[500] },
                children: [{ name: "JavaScript", itemStyle: { color: BEIGE[400] } }],
              },
              {
                name: "AI・データ分析",
                itemStyle: { color: BEIGE[500] },
                children: [{ name: "Python", itemStyle: { color: BEIGE[400] } }],
              },
              {
                name: "iPhoneアプリ",
                itemStyle: { color: BEIGE[500] },
                children: [{ name: "Swift", itemStyle: { color: BEIGE[400] } }],
              },
              {
                name: "Androidアプリ",
                itemStyle: { color: BEIGE[500] },
                children: [{ name: "Kotlin", itemStyle: { color: BEIGE[400] } }],
              },
            ],
          },
        ],
        top: "5%",
        left: "10%",
        bottom: "5%",
        right: "10%",
        symbolSize: 10,
        label: {
          position: "left",
          verticalAlign: "middle",
          align: "right",
          fontSize: 12,
          fontWeight: "bold",
          color: BEIGE[700],
        },
        leaves: {
          label: {
            position: "right",
            verticalAlign: "middle",
            align: "left",
          },
        },
        emphasis: {
          focus: "descendant",
        },
        expandAndCollapse: false,
        animationDuration: 550,
        animationDurationUpdate: 750,
        lineStyle: {
          color: BEIGE[300],
          width: 1.5,
        },
      },
    ],
  };
}

// 学習ロードマップ用のプリセット
function getLearningRoadmapChart(): any {
  return {
    tooltip: {
      trigger: "item",
      formatter: (params: any) => {
        const descriptions: Record<string, string> = {
          "STEP 1: 基本構文": "console.log, 変数, 条件分岐 (今ここ！)",
          "STEP 2: 関数": "コードを再利用可能にまとめる",
          "STEP 3: オブジェクト": "データをグループ化して管理",
          "STEP 4: DOM操作": "Webページの要素を動的に変更",
          "STEP 5: 非同期処理": "APIからデータを取得",
          "STEP 6: フレームワーク": "React, Vue.jsで本格開発",
        };
        return descriptions[params.name] || params.name;
      },
    },
    grid: {
      left: "3%",
      right: "10%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "value",
      max: 100,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: "category",
      data: [
        "STEP 6: フレームワーク",
        "STEP 5: 非同期処理",
        "STEP 4: DOM操作",
        "STEP 3: オブジェクト",
        "STEP 2: 関数",
        "STEP 1: 基本構文",
      ],
      axisLabel: {
        fontSize: 11,
        fontWeight: "bold",
        color: BEIGE[700],
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    series: [
      {
        type: "bar",
        data: [
          { value: 60, itemStyle: { color: BEIGE[200] } },
          { value: 70, itemStyle: { color: BEIGE[200] } },
          { value: 80, itemStyle: { color: BEIGE[200] } },
          { value: 85, itemStyle: { color: BEIGE[200] } },
          { value: 90, itemStyle: { color: BEIGE[200] } },
          { value: 100, itemStyle: { color: BEIGE[700] } },
        ],
        barWidth: 20,
        label: {
          show: true,
          position: "right",
          formatter: (params: any) => {
            if (params.dataIndex === 5) return "← 今ここ！";
            return "";
          },
          fontSize: 12,
          fontWeight: "bold",
          color: BEIGE[700],
        },
        emphasis: {
          itemStyle: {
            color: BEIGE[800],
          },
        },
      },
    ],
  };
}

export function InteractiveChart({
  type,
  data,
  title,
  height = "300px",
  customOptions,
}: InteractiveChartProps) {
  const options = useMemo(() => {
    // カスタムオプションが渡された場合はそのまま使用
    if (customOptions) {
      return {
        ...customOptions,
        title: title ? { text: title, left: "center", textStyle: { fontSize: 14, fontWeight: "bold" } } : customOptions.title,
      };
    }

    // プリセットチャート
    if (type === "flowchart") {
      return getLanguageSelectionFlowchart();
    }

    if (type === "roadmap") {
      const roadmapOptions = getLearningRoadmapChart();
      if (title) {
        roadmapOptions.title = { text: title, left: "center", textStyle: { fontSize: 14, fontWeight: "bold", color: BEIGE[700] } };
      }
      return roadmapOptions;
    }

    // 基本的なチャート
    const baseOptions: any = {
      title: title ? { text: title, left: "center", textStyle: { fontSize: 14, fontWeight: "bold", color: BEIGE[700] } } : undefined,
      tooltip: { trigger: type === "pie" ? "item" : "axis" },
      grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
    };

    if (type === "line" || type === "bar") {
      baseOptions.xAxis = {
        type: "category",
        data: data?.map((d) => d.name) || [],
        axisLabel: { color: BEIGE[500] },
        axisLine: { lineStyle: { color: BEIGE[200] } },
      };
      baseOptions.yAxis = {
        type: "value",
        axisLabel: { color: BEIGE[500] },
        axisLine: { lineStyle: { color: BEIGE[200] } },
        splitLine: { lineStyle: { color: BEIGE[100] } },
      };
      baseOptions.series = [
        {
          type,
          data: data?.map((d) => d.value) || [],
          itemStyle: { color: BEIGE[500] },
          areaStyle: type === "line" ? { opacity: 0.1, color: BEIGE[300] } : undefined,
        },
      ];
    } else if (type === "pie") {
      baseOptions.series = [
        {
          type: "pie",
          radius: ["40%", "70%"],
          data: data || [],
          itemStyle: {
            color: (params: any) => {
              const colors = [BEIGE[700], BEIGE[500], BEIGE[400], BEIGE[300], BEIGE[200]];
              return colors[params.dataIndex % colors.length];
            },
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        },
      ];
    }

    return baseOptions;
  }, [type, data, title, customOptions]);

  return (
    <div className="my-6 rounded-xl border overflow-hidden bg-white" style={{ borderColor: BEIGE[200] }}>
      {title && (
        <div className="px-4 py-3 border-b" style={{ backgroundColor: "#fafafa", borderColor: BEIGE[200] }}>
          <h4 className="text-sm font-bold" style={{ color: BEIGE[700] }}>{title}</h4>
        </div>
      )}
      <div className="p-4" style={{ height }}>
        <Suspense
          fallback={
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: BEIGE[400] }} />
            </div>
          }
        >
          <ReactECharts
            option={options}
            style={{ height: "100%", width: "100%" }}
            opts={{ renderer: "svg" }}
          />
        </Suspense>
      </div>
    </div>
  );
}
